

import {
  Megaphone, BarChart3, Target,
  Images, Layout, BadgeDollarSign
} from 'lucide-react';
import { Template as SpotlightTemplate } from '../../types/template.types';
import QuickActionsRender from './render';

export interface QuickAction {
  icon: JSX.Element;
  text: string;
  description: string;
  templates?: SpotlightTemplate[];
  isNew?: boolean;
  disabled?: boolean;
  link?: string;
  comingSoon?: boolean;
  badge?: string;
  href?: string;
  newTabHref?: string;
  section?: string;
  template?: SpotlightTemplate;
  onClick?: () => void;
  isPopular?: boolean;
}

export const quickActions: QuickAction[] = [
  {
    icon: <Megaphone className="w-4 h-4" />,
    text: "Create Campaigns",
    description: "Start with a pre-configured campaign or build a custom campaign",
    onClick: () => {
      const url = `/dashboard/wizard`;
      window.open(url, '_blank');
    },
    badge: "Popular",
    isPopular: true
  },
  {
    icon: <Images className="w-4 h-4" />,
    text: "Ads Builder",
    description: "Generate hundreds of ads across multiple campaigns in seconds",
    isNew: true,
    badge: "New",
    href: "/dashboard/ads-builder?section=ads-builder",
    newTabHref: "/dashboard/ads-builder?section=ads-builder"
  },
  {
    icon: <Layout className="w-4 h-4" />,
    text: "Landing Page Builder",
    description: "Create and host stunning landing pages with our intuitive visual editor",
    isNew: true,
    badge: "New",
    href: "/dashboard/landing-pages",
    newTabHref: "/dashboard/landing-pages"
  },
  {
    icon: <BarChart3 className="w-4 h-4" />,
    text: "Generate Drilldown Report",
    description: "Create a detailed analytics report with performance metrics, trends and insights",
    href: "/dashboard/drilldown-reports",
    newTabHref: "/dashboard/drilldown-reports"
  },
  {
    icon: <BadgeDollarSign className="w-4 h-4" />,
    text: "Cost Update",
    description: "Configure and manage cost settings for individual and grouped campaigns.",
    href: "/dashboard/cost-update",
    newTabHref: "/dashboard/cost-update"
  },
  {
    icon: <Target className="w-4 h-4" />,
    text: "Conversions Update",
    description: "Set and adjust rules to your conversions filters on campaigns.",
    href: "/conversions",
    newTabHref: "/conversions"
  }
];

export interface QuickActionsProps {
  searchQuery: string;
  onClose: () => void;
  showMoreOptions?: boolean;
}

export const filterQuickActions = (items: QuickAction[], query: string) => {
  if (!query) return items;
  const lowerQuery = query.toLowerCase();
  return items.filter(item =>
    item.text?.toLowerCase().includes(lowerQuery) ||
    item.description?.toLowerCase().includes(lowerQuery)
  );
};

export default function QuickActions({
  searchQuery,
  onClose,
  showMoreOptions = false,
}: QuickActionsProps) {
  const filteredQuickActions = filterQuickActions(quickActions, searchQuery);

  const handleActionClick = (action: QuickAction) => {
    if (action.disabled) return;

    // Handle onClick actions
    if (action.onClick) {
      action.onClick();
      onClose();
      return;
    }

    // Handle link-based actions
    if (action.link) {
      window.open(action.link, '_blank');
      onClose();
      return;
    }

    // Handle href-based actions (navigation handled by render component)
    if (action.href) {
      onClose();
      return;
    }
  };

  if (!filteredQuickActions.length) {
    return null;
  }

  return (
    <QuickActionsRender
      actions={filteredQuickActions}
      onActionClick={handleActionClick}
    />
  );
}
